// TMOS User Approval Page
// Date: December 19, 2025
// Purpose: Main page for tenant admins to approve/reject user registration requests
//
// Critical Functionality:
// - Tenant provisioning: Approve new tenants or merge to existing
// - User provisioning: Approve individual users with role/hierarchy assignment
// - Self-service setup: Enable users to complete their setup after approval

import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { userApprovalService, UserApprovalRequest, ApprovalStats } from '@/services/userApprovalService';
import { UserApprovalList } from '@/components/user-approval/UserApprovalList';
import { UserApprovalStats } from '@/components/user-approval/UserApprovalStats';
import { toast } from 'react-hot-toast';

export const UserApprovalPage: React.FC = () => {
  const { t } = useLanguage();
  const [approvals, setApprovals] = useState<UserApprovalRequest[]>([]);
  const [stats, setStats] = useState<ApprovalStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    loadData();
  }, [page, refreshKey]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load approvals and stats in parallel
      const [approvalsData, statsData] = await Promise.all([
        userApprovalService.getPendingApprovals(page, 20),
        userApprovalService.getApprovalStats()
      ]);

      setApprovals(approvalsData.approvals);
      setTotalPages(approvalsData.pagination.pages);
      setStats(statsData);
    } catch (error: any) {
      console.error('Error loading approval data:', error);
      toast.error(error.message || 'Failed to load approval requests');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[var(--color-surface-text)] mb-2">
            {t('userApproval.title') || 'User Approval Management'}
          </h1>
          <p className="text-[var(--color-secondary-text)]">
            {t('userApproval.description') || 
              'Review and approve user registration requests. Assign roles, hierarchies, and positions to provision users for self-service setup.'}
          </p>
        </div>

        {/* Statistics */}
        {stats && (
          <UserApprovalStats stats={stats} />
        )}

        {/* Approval List */}
        <div className="mt-6">
          <UserApprovalList
            approvals={approvals}
            loading={loading}
            onApprove={handleRefresh}
            onReject={handleRefresh}
            onMerge={handleRefresh}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </div>
      </div>
    </div>
  );
};
